/**
 * `/settings/advanced/*` — #44574.
 *
 * Ports of:
 *   settings/advanced/danger
 *   settings/advanced/data
 *   settings/advanced/mcp
 *   settings/advanced/performance  (wire existing PerformancePanel)
 *
 * Live Next pages under excellent/app/settings/advanced/** are UNTOUCHED (D6).
 */

import * as React from "react";
import { createRoute } from "@tanstack/react-router";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  SettingsPageShell,
  SettingsSection,
} from "@excellent/ui";
import { apiCall } from "@excellent/ui/api";
import { residency } from "@excellent/ui/api/contracts/residency";
import { PerformancePanel } from "../shared/settings/performance-panel";
import { rootRoute } from "./__root";

// ── /settings/advanced/danger ───────────────────────────────────────────────

function AdvancedDanger() {
  return (
    <SettingsPageShell
      title="Danger zone"
      description="Destructive operations. Read each line carefully."
    >
      <Alert variant="destructive">
        <AlertTitle>These actions cannot be undone.</AlertTitle>
        <AlertDescription>
          Back up <code>~/.excellent/data.db</code> before running anything on
          this page.
        </AlertDescription>
      </Alert>

      <SettingsSection
        id="reseed"
        title="Reseed a product"
        description="Re-apply a product's default seed. Idempotent — recorded in _seeds. Use each product's settings page."
      >
        <p className="type-body text-muted-foreground">
          Seed actions live per-capability (e.g.{" "}
          <a className="underline" href="/crm/settings/seed">
            CRM → Seed
          </a>
          ). There is no global “reseed everything” button by design.
        </p>
      </SettingsSection>
    </SettingsPageShell>
  );
}

// ── /settings/advanced/data ─────────────────────────────────────────────────

function AdvancedData() {
  const [exporting, setExporting] = React.useState(false);
  const [exportMsg, setExportMsg] = React.useState<string | null>(null);

  async function onExport() {
    setExporting(true);
    setExportMsg(null);
    try {
      const data = await apiCall(residency.export, {});
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `excellent-workspace-export-${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);
      setExportMsg("Export downloaded.");
    } catch (e) {
      setExportMsg(e instanceof Error ? e.message : String(e));
    } finally {
      setExporting(false);
    }
  }

  return (
    <SettingsPageShell
      title="Data & residency"
      description="Excellent is local-first. Every byte — including encrypted credentials — lives in one SQLite file on this machine, custodied by no third party. No graduation, no lock-in. You can leave at any time and take the file with you."
    >
      <SettingsSection
        id="residency"
        title="Residency report"
        description="Portable workspace export for the ‘you already own your data’ download."
      >
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="success">external custody: none</Badge>
          <Badge variant="success">portable</Badge>
        </div>
        <p className="mt-3 type-body text-muted-foreground">
          Download a JSON export of this workspace. Secrets stay sealed; the
          file is yours to take to another machine.
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mt-3"
          disabled={exporting}
          onClick={() => void onExport()}
        >
          {exporting ? "Exporting…" : "Download workspace export"}
        </Button>
        {exportMsg ? (
          <p className="mt-2 type-body text-muted-foreground">{exportMsg}</p>
        ) : null}
      </SettingsSection>
    </SettingsPageShell>
  );
}

// ── /settings/advanced/mcp ──────────────────────────────────────────────────

function AdvancedMcp() {
  return (
    <SettingsPageShell
      title="MCP servers"
      description={
        <span>
          Model Context Protocol servers configured for local agent hosts. The
          live list is read from{" "}
          <code className="type-subtext">~/.claude/settings.json</code> on the Next
          host; this port surfaces the same shell and guidance.
        </span>
      }
    >
      <SettingsSection
        id="mcp-servers"
        title="Configured servers"
        description="MCP servers registered for Claude Code and other local hosts."
      >
        <p className="type-body text-muted-foreground">
          Edit <code>~/.claude/settings.json</code> →{" "}
          <code>mcpServers</code> to add or remove servers. Restart the agent
          host after changes. Excellent also ships its own MCP surface via{" "}
          <code>excellent-mcp</code>.
        </p>
      </SettingsSection>
    </SettingsPageShell>
  );
}

// ── /settings/advanced/performance ──────────────────────────────────────────

function AdvancedPerformance() {
  return (
    <SettingsPageShell
      title="Performance"
      description={
        <span>
          Index every internal API endpoint (shell + installed products) and
          benchmark them against the running local server. The run captures
          per-endpoint p50/p95/max latencies and persists snapshots under{" "}
          <code>docs/perf/history/</code> so regressions become visible across
          sessions. See <code>docs/perf/PRD.md</code> for the design.
        </span>
      }
    >
      <PerformancePanel />
    </SettingsPageShell>
  );
}

export const settingsAdvancedDangerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/advanced/danger",
  component: AdvancedDanger,
});

export const settingsAdvancedDataRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/advanced/data",
  component: AdvancedData,
});

export const settingsAdvancedMcpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/advanced/mcp",
  component: AdvancedMcp,
});

export const settingsAdvancedPerformanceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/advanced/performance",
  component: AdvancedPerformance,
});
